import { Text } from '@sketch/components'
import { breakpoint } from '@sketch/global-styles'
import styled, { css } from 'styled-components'

export const Title = styled(Text).attrs({ $reset: true, as: 'h1' })`
  grid-area: header;
  align-content: center;
  pointer-events: none;

  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  font-size: 0.875rem;
  font-weight: 600;
  font-style: semibold;
  line-height: 1.142; /* stylelint-disable-line scales/line-heights*/
`

export const Price = styled.span<{ $currencyOrder: 'before' | 'after' }>`
  grid-area: price;
  display: inline-flex;
  margin: auto;
  pointer-events: none;

  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  font-size: 2rem;
  line-height: 1.063; /* stylelint-disable-line scales/line-heights*/
  font-weight: 500;
  font-style: medium;

  ::after {
    content: attr(data-currency);
    font-size: 1.5rem;
    line-height: 1.2;
    vertical-align: top;
    display: inline-block;

    ${({ $currencyOrder }) =>
      $currencyOrder === 'before' &&
      css`
        order: -1;
      `}
  }

  ${breakpoint('sm')`
    font-size: 3rem;
    line-height: 1.042;

    ::after {
      margin-top: 2px;
      font-size: 32px;
      line-height: 1.2;
    }
  `}
`

export const PriceCents = styled.span`
  font-size: 1.5rem;
  line-height: 1.2; /* stylelint-disable-line scales/line-heights*/
  vertical-align: top;
  display: inline-block;
`

export const Description = styled(Text).attrs({
  as: 'span',
  textStyle: 'copy.quaternary.standard.E',
})`
  grid-area: description;
  align-content: center;
  pointer-events: none;

  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.42; /* stylelint-disable-line scales/line-heights*/
`

export const Input = styled.input.attrs({
  type: 'radio',
})`
  grid-area: input;
  align-self: center;

  appearance: none;
  background-color: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  box-shadow: inset ${({ theme }) => theme.shadows.boxShadow.subtle};

  width: 16px;
  height: 16px;
  border-radius: 8px;
  cursor: pointer;

  ::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;

    box-shadow:
      0 0 0 1px ${({ theme }) => theme.colors.shadow.outer},
      0 0 1px 0 inset ${({ theme }) => theme.colors.border.A};
    background: ${({ theme }) => theme.colors.background.secondary.B};
    border-radius: 8px;
  }

  :disabled {
    cursor: not-allowed;

    &
      ~ ${/* sc-selector */ Title},
      &
      ~ ${/* sc-selector */ Price},
      &
      ~ ${/* sc-selector */ Description} {
      color: ${({ theme }) => theme.colors.state.disabled};
    }
  }

  :checked {
    border: none;
    background: ${({ theme }) => theme.colors.sketch.A};
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1);

    ::before {
      width: 6px;
      height: 6px;
      content: ' ';
      background: white;
      display: block;
      margin: 5px; /* stylelint-disable-line scales/space */
      border-radius: 50%;
    }

    ::after {
      background: ${({ theme }) => theme.colors.background.secondary.A};
      box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.sketch.A};
    }
  }

  :hover:not(:disabled):not(:checked) {
    ::after {
      background: ${({ theme }) => theme.colors.background.secondary.A};
      box-shadow:
        0 0 0 1px ${({ theme }) => theme.colors.shadow.outer},
        0 0 1px 0 inset ${({ theme }) => theme.colors.border.A};
    }
  }

  ${breakpoint('sm')`
    ::after {
        border-radius: 20px;
    }
  `}
`

export const Wrapper = styled.label`
  display: grid;
  width: 100%;
  position: relative;
  z-index: 0; /* stylelint-disable-line scales/z-indices */

  grid-template-columns: auto 2fr auto;
  gap: 4px 16px;
  grid-template-areas:
    'input header price'
    'input description price';

  padding: 12px;

  ${breakpoint('base', 'sm')`
    ${Title}, ${Description} {
     justify-self: left;
    }

    ${Price} {
      align-content: center;
    }
  `}

  ${breakpoint('sm')`
    grid-template-columns: 1fr;
    gap: 0;
    grid-template-areas:
      "input"
      "header"
      "price"
      "description";

    padding: 13px;
    justify-items: center;

    ${Input} {
      justify-self: left;
    }

    ${Title}, ${Price}, ${Description} {
      margin-bottom: 10px;
    }
  `}

  ${/* sc-selector */ Input}:checked {
    & ~ ${/* sc-selector */ Title}, & ~ ${/* sc-selector */ Price} {
      color: ${({ theme }) => theme.colors.foreground.secondary.A};
    }
  }
`
